import axios from 'axios';
import { createNode, isShapeValue } from '@glueapp/graph-types';
import type { DataValue } from '@glueapp/graph-types';

/**
 * All inPorts that may be present on an HttpRequest node at execution time.
 * The static declaration only includes the 4 configuration ports; auth, body,
 * and pattern-based header/query/path ports are added dynamically by computeShape.
 */
interface HttpRequestExecutePorts {
    URL: DataValue;
    Method: DataValue;
    'Response Shape': DataValue;
    'Body Shape': DataValue;
    // Auth ports — present based on authType state
    'Auth Token': DataValue;
    'Auth Username': DataValue;
    'Auth Password': DataValue;
    'Auth Key': DataValue;
    'Auth Token URL': DataValue;
    'Auth Client ID': DataValue;
    'Auth Client Secret': DataValue;
    // Body port — present for POST/PUT/DELETE/PATCH
    Body: DataValue;
    // Pattern ports accessed via string variable in for-in loops
    [key: string]: DataValue;
}

const BODY_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'];

function extractPathParams(url: string): string[] {
    // Only extract from the path portion — not query string or fragment
    const pathPortion = url.split(/[?#]/)[0];
    const params = new Set<string>();
    // Match :paramName (Express style)
    for (const match of pathPortion.matchAll(/:([a-zA-Z_][a-zA-Z0-9_]*)/g)) {
        params.add(match[1]);
    }
    // Match {paramName} (OpenAPI style) but not ${paramName} (template literals)
    for (const match of pathPortion.matchAll(/(?<!\$)\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g)) {
        params.add(match[1]);
    }
    return [...params];
}

export const HttpRequest = createNode({
    id: 'Http Request',
    name: 'Http Request',
    description: 'Performs an HTTP request with dynamic headers, query params, authentication, and typed body/response.',
    aiDescription: [
        'Makes HTTP requests. Setup steps:',
        '1. Set URL value: glue set-value <id> URL "https://api.example.com/users/:userId"',
        '   - Path params (:param or {param} syntax) in the URL automatically create "Path: <name>" input ports.',
        '   - Example: URL "https://api.example.com/users/:userId" creates a "Path: userId" input port.',
        '2. Set Method value: glue set-value <id> Method "POST"',
        '   - Allowed values: GET, POST, PUT, DELETE, PATCH',
        '   - "Body Shape" input port + "Body" input port only appear for POST, PUT, DELETE, PATCH.',
        '   - GET hides both body-related ports.',
        '3. (Optional) Type the response — set "Response Shape" to a shape value:',
        '   glue set-value <id> "Response Shape" \'{"isShapeValue":true,"type":{"kind":"object","properties":{"id":{"kind":"number"},"name":{"kind":"string"}},"restriction":"open"}}\'',
        '   This retypes the "Response" output port from string to the specified type.',
        '   IMPORTANT: a JSON response body is AUTO-PARSED — "Response" gives you an object/array, not a JSON string.',
        '   Do NOT feed "Response" into a ParseJSON node (that stringifies it to "[object Object]" and fails). Use "Response Shape" to type it instead, and prefer "restriction":"open" so it satisfies generic object ports.',
        '4. (Optional) Type the request body — set "Body Shape" to a shape value (only when method has a body):',
        '   glue set-value <id> "Body Shape" \'{"isShapeValue":true,"type":{"kind":"object","properties":{"name":{"kind":"string"},"email":{"kind":"string"}},"restriction":"open"}}\'',
        '   This retypes the "Body" input port from string to the specified type.',
        '5. Add headers via state — headers are managed through node state, not port values:',
        '   glue set-state <id> headers \'[{"id":1,"name":"Content-Type"},{"id":2,"name":"Authorization"}]\'',
        '   This creates "Header: Content-Type" and "Header: Authorization" input ports (string type).',
        '   Then set their values: glue set-value <id> "Header: Content-Type" "application/json"',
        '6. Add query params via state:',
        '   glue set-state <id> queryParams \'[{"id":1,"name":"page"},{"id":2,"name":"limit"}]\'',
        '   This creates "Query: page" and "Query: limit" input ports (string type).',
        '7. Enable auth via state:',
        '   glue set-state <id> authEnabled true',
        '   glue set-state <id> authType "bearer"',
        '   Auth types: bearer (creates "Auth Token" port), basic (creates "Auth Username" + "Auth Password" ports),',
        '   apikey (creates "Auth Key" port; set header name with: glue set-state <id> apiKeyHeaderName "X-API-Key"),',
        '   oauth2 (creates "Auth Token URL" + "Auth Client ID" + "Auth Client Secret" ports).',
        '8. Always run glue shape <id> after setting values/state to verify the resulting ports.',
        '',
        'Output ports: "Response" (typed by Response Shape or string), "Status Code" (number).',
    ].join('\n'),
    category: 'Http',
    color: '#d58f29',
    integration: undefined,

    inPorts: {
        URL: { kind: 'string' },
        Method: {
            kind: 'union',
            subTypes: [
                { kind: 'stringLiteral', stringLiteral: 'GET' },
                { kind: 'stringLiteral', stringLiteral: 'POST' },
                { kind: 'stringLiteral', stringLiteral: 'PUT' },
                { kind: 'stringLiteral', stringLiteral: 'DELETE' },
                { kind: 'stringLiteral', stringLiteral: 'PATCH' },
            ],
        },
        'Response Shape': { kind: 'shape', restriction: undefined },
        'Body Shape': { kind: 'shape', restriction: undefined },
    },

    outPorts: {
        Response: { kind: 'string' },
        'Status Code': { kind: 'number' },
    },

    computeShape: input => {
        const methodValue = input.portValues['Method'];
        const responseShapeValue = input.portValues['Response Shape'];
        const bodyShapeValue = input.portValues['Body Shape'];
        const urlValue = input.portValues['URL'];

        const hasBody = typeof methodValue === 'string' && BODY_METHODS.includes(methodValue);

        // Start with base inPorts, conditionally filter Body Shape
        let basePorts = hasBody ? input.nodeInPorts : input.nodeInPorts.filter(p => p.name !== 'Body Shape');

        // Derive header/query/auth ports from state (not preResolvedInputPorts)
        const headers = (input.state?.headers as Array<{ id: number; name: string }>) || [];
        const queryParams = (input.state?.queryParams as Array<{ id: number; name: string }>) || [];
        const authEnabled = input.state?.authEnabled as boolean;
        const authType = (input.state?.authType as string) || 'bearer';

        const stateDerivedPorts: Array<{ name: string; type: { kind: 'string' } }> = [];
        for (const header of headers) {
            if (header.name) stateDerivedPorts.push({ name: `Header: ${header.name}`, type: { kind: 'string' as const } });
        }
        for (const param of queryParams) {
            if (param.name) stateDerivedPorts.push({ name: `Query: ${param.name}`, type: { kind: 'string' as const } });
        }
        if (authEnabled) {
            switch (authType) {
                case 'bearer':
                    stateDerivedPorts.push({ name: 'Auth Token', type: { kind: 'string' as const } });
                    break;
                case 'basic':
                    stateDerivedPorts.push(
                        { name: 'Auth Username', type: { kind: 'string' as const } },
                        { name: 'Auth Password', type: { kind: 'string' as const } },
                    );
                    break;
                case 'apikey':
                    stateDerivedPorts.push({ name: 'Auth Key', type: { kind: 'string' as const } });
                    break;
                case 'oauth2':
                    stateDerivedPorts.push(
                        { name: 'Auth Token URL', type: { kind: 'string' as const } },
                        { name: 'Auth Client ID', type: { kind: 'string' as const } },
                        { name: 'Auth Client Secret', type: { kind: 'string' as const } },
                    );
                    break;
            }
        }

        // Derive path ports from the current URL
        const pathPorts: Array<{ name: string; type: { kind: 'string' } }> = [];

        if (typeof urlValue === 'string' && urlValue.length > 0) {
            for (const param of extractPathParams(urlValue)) {
                pathPorts.push({ name: `Path: ${param}`, type: { kind: 'string' as const } });
            }
        }

        // Build inPorts: base ports, then path params right after URL, then body, then headers/query/auth
        const urlIndex = basePorts.findIndex(p => p.name === 'URL');
        const inPorts = [
            ...basePorts.slice(0, urlIndex + 1),
            ...pathPorts,
            ...basePorts.slice(urlIndex + 1),
            ...(hasBody ? [{ name: 'Body', type: isShapeValue(bodyShapeValue) ? bodyShapeValue.type : { kind: 'string' as const } }] : []),
            ...stateDerivedPorts,
        ];

        // Build outPorts — retype Response if Response Shape is set
        let outPorts = input.nodeOutPorts;
        if (isShapeValue(responseShapeValue)) {
            outPorts = outPorts.map(p => (p.name === 'Response' ? { name: 'Response', type: responseShapeValue.type } : p));
        }

        return { inPorts, outPorts };
    },

    isAsync: true,

    execute: async function (input) {
        const inPorts = input.inPorts as HttpRequestExecutePorts;
        let url = inPorts.URL as string | undefined;
        const method = (inPorts.Method as string) || 'GET';

        if (!url) {
            return {
                output: { Response: 'FAILED REQUEST: No URL provided', 'Status Code': 0 },
            };
        }

        // Substitute path params
        for (const portName in inPorts) {
            if (portName.startsWith('Path: ')) {
                const paramName = portName.slice('Path: '.length);
                const value = String(inPorts[portName] ?? '');
                url = url.replaceAll(`:${paramName}`, encodeURIComponent(value));
                url = url.replaceAll(`{${paramName}}`, encodeURIComponent(value));
            }
        }

        // Build query string
        const queryParts: string[] = [];
        for (const portName in inPorts) {
            if (portName.startsWith('Query: ')) {
                const paramName = portName.slice('Query: '.length);
                const value = String(inPorts[portName] ?? '');
                queryParts.push(`${encodeURIComponent(paramName)}=${encodeURIComponent(value)}`);
            }
        }
        if (queryParts.length > 0) {
            const separator = url.includes('?') ? '&' : '?';
            url = url + separator + queryParts.join('&');
        }

        // Collect headers
        const headers: Record<string, string> = {};
        for (const portName in inPorts) {
            if (portName.startsWith('Header: ')) {
                const headerName = portName.slice('Header: '.length);
                headers[headerName] = String(inPorts[portName] ?? '');
            }
        }

        // Apply auth
        if (input.state?.authEnabled) {
            const authType = input.state?.authType as string | undefined;
            switch (authType) {
                case 'bearer': {
                    const token = inPorts['Auth Token'];
                    if (token) headers['Authorization'] = `Bearer ${token}`;
                    break;
                }
                case 'basic': {
                    const username = String(inPorts['Auth Username'] ?? '');
                    const password = String(inPorts['Auth Password'] ?? '');
                    const encoded = Buffer.from(`${username}:${password}`).toString('base64');
                    headers['Authorization'] = `Basic ${encoded}`;
                    break;
                }
                case 'apikey': {
                    const headerName = (input.state?.apiKeyHeaderName as string) || 'X-API-Key';
                    const apiKey = inPorts['Auth Key'];
                    if (apiKey) headers[headerName] = String(apiKey);
                    break;
                }
                case 'oauth2': {
                    const tokenUrl = String(inPorts['Auth Token URL'] ?? '');
                    const clientId = String(inPorts['Auth Client ID'] ?? '');
                    const clientSecret = String(inPorts['Auth Client Secret'] ?? '');
                    if (tokenUrl && clientId && clientSecret) {
                        try {
                            const tokenResponse = await axios.post(
                                tokenUrl,
                                new URLSearchParams({
                                    grant_type: 'client_credentials',
                                    client_id: clientId,
                                    client_secret: clientSecret,
                                }).toString(),
                                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
                            );
                            const accessToken = tokenResponse.data?.access_token;
                            if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
                        } catch (error) {
                            return {
                                output: {
                                    Response: `OAuth2 token request failed: ${error instanceof Error ? error.message : String(error)}`,
                                    'Status Code': 0,
                                },
                            };
                        }
                    }
                    break;
                }
            }
        }

        // Body
        const body = inPorts.Body ?? null;

        try {
            const response = await axios({
                url,
                method: method.toLowerCase(),
                data: body,
                headers: Object.keys(headers).length > 0 ? headers : undefined,
            });
            return {
                output: {
                    Response: response.data,
                    'Status Code': response.status,
                },
            };
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                const status = error.response?.status ?? 0;
                const data = error.response?.data;
                return {
                    output: {
                        Response: data != null ? data : error.message,
                        'Status Code': status,
                    },
                };
            }
            return {
                output: {
                    Response: error instanceof Error ? error.message : String(error),
                    'Status Code': 0,
                },
            };
        }
    },
});
